import { parseTagString, type TagValue, tagToString } from '@opportunities/list/-components/TagSelect'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { getOpportunityTags, updateOpportunityTags } from '@/lib'
import { updateOpportunityListCache } from '@/lib/queryClient/optimisticUpdate'
import { queryKeys } from '@/lib/queryKeys'

/**
 * TagsDialogのロジック層を管理するカスタムhook
 *
 * オポチュニティのタグ編集に必要な状態管理、データ取得、更新処理を提供する
 */
export function useTagsDialog(
  open: boolean,
  onOpenChange: (open: boolean) => void,
  opportunity: { id: number; title: string } | null,
) {
  const [selectedTags, setSelectedTags] = useState<TagValue[]>([])
  const queryClient = useQueryClient()

  // ダイアログが閉じたときに状態をリセット
  useEffect(() => {
    if (!open) {
      setSelectedTags([])
    }
  }, [open])

  // 既存タグを取得（オポチュニティに設定済みのタグ）
  const { data: tagsData } = useQuery({
    queryKey: queryKeys.opportunity.tags(opportunity?.id),
    queryFn: async () => {
      if (!opportunity?.id) throw new Error('No opportunity ID')
      return getOpportunityTags(opportunity.id)
    },
    enabled: !!opportunity?.id && open,
  })

  // タグ更新
  const updateMutation = useMutation({
    mutationFn: async (tags: TagValue[]) => {
      if (!opportunity?.id) throw new Error('No opportunity ID')
      const validTags = tags.map(tagToString).filter((tag) => tag?.trim())
      return updateOpportunityTags(
        opportunity.id,
        validTags.map((tag) => ({ name: tag })),
      )
    },
    onSuccess: (response) => {
      toast.success('タグを更新しました')
      // タグ詳細のキャッシュを無効化
      queryClient.invalidateQueries({ queryKey: queryKeys.opportunity.tags(opportunity?.id) })

      // 一覧のキャッシュを楽観的更新（タグは一覧に表示されるため）
      if (opportunity?.id) {
        // タグ名から color (グループ名) を抽出
        const updatedTags = response.tags.map((tag) => {
          // タグ名が "group:name" 形式の場合、group を color として使用
          const match = tag.name.match(/^([a-z]+):/)
          const color = match?.[1] ?? null

          return {
            id: tag.id,
            name: tag.name,
            color, // タグ名から抽出したグループ名
          }
        })

        updateOpportunityListCache(queryClient, opportunity.id, () => ({
          tags: updatedTags,
        }))
      }

      onOpenChange(false)
    },
    onError: () => {
      toast.error('タグの更新に失敗しました')
    },
  })

  // 既存タグをフォームに設定
  useEffect(() => {
    // 閉じたときはリセット用effectの結果を打ち消さないよう、開いているときだけ反映する
    if (open && tagsData?.tags && Array.isArray(tagsData.tags)) {
      const tagNames: TagValue[] = tagsData.tags
        .filter((tag) => tag?.name)
        .map((tag) => {
          if (tag.group) {
            return { id: tag.tag_id, group: tag.group, name: tag.name }
          }
          const parsed = parseTagString(tag.name)
          return { ...parsed, id: tag.tag_id }
        })
      setSelectedTags(tagNames)
    }
  }, [tagsData, open])

  const handleSave = () => {
    updateMutation.mutate(selectedTags)
  }

  return {
    selectedTags,
    setSelectedTags,
    isPending: updateMutation.isPending,
    handleSave,
  }
}
